// The allocation control, for every kind, in one component.
//
// It used to live inside the detail page, which meant a dial move cost a
// navigation: the register listed experiments and the control was somewhere
// else. The Prompt sections page has always worked the other way round, every
// section with its own control on the row, and that is the page operators
// actually use. So this is the control, and both surfaces mount it: expanded in
// place on a register row, and as its own card on the detail page.
//
// One control, six shapes, all of them `{name: pct}` because that is what
// `POST /api/admin/experimentation/{key}/allocation` takes:
//
//   section   one dial, the share who get the section at all
//   prompt    one slider per non-default approach, the default takes the rest
//   injection three dials: exposure, send when triggered, send when not
//   ui        one slider per non-baseline arm, the baseline takes the rest
//   logic     one slider per non-default arm
//   config    one slider per value arm, labelled with the value it sets
//
// Everything that made a move auditable stays: the reason is mandatory, the
// write appends a lifecycle row with the actor, and the canary rule refuses a
// first-day ramp past ten percent unless the reason says `skip-canary:`. The
// refusal is the SERVER's; this page does not reimplement it, it renders what
// came back.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button, TableExport } from "@recruiter/shared";
import type { AdminExperimentRecord } from "@recruiter/shared";

import { setAllocation } from "../lib/api";
import {
  type AllocatableKind,
  type AllocationDraft,
  INJECTION_DIAL_LABELS,
  SECTION_DIAL_KEY,
  allocatableKind,
  allocationDraftFrom,
  allocationMoved,
  armShares,
  describeAllocationMove,
  formatCalls,
  liveAllocationKey,
  overlayAllocation,
  projectedShares,
  validateAllocation,
} from "../lib/experimentation";
import { adminKeys, allocationQuery } from "../lib/queries";
import { ExplorationNote, ShadowPreset } from "./experiment-controls";
import { errorMessage } from "./states";
import "./experiment-allocation.css";

/** The one sentence that says what this kind's sliders mean. Written per kind
 *  rather than generically, because "share of users on this arm" and "share of
 *  enrolled turns where the trigger fired" are not the same claim and an
 *  operator moving a dial has to know which one they are moving. */
const KIND_BLURB: Record<AllocatableKind, string> = {
  ui: "The share of users on each non-baseline arm; the baseline takes the rest. Assignment is sticky per user, so raising a dial only adds people and lowering it removes exactly the same ones.",
  section:
    "The share of users whose prompt carries this section at all. 100% is the shipped prompt for everybody; below that, the rest get the prompt without it and are stamped into the absent arm.",
  prompt:
    "The share of projects that get each version of the text; the default takes the rest. Sticky per project.",
  injection:
    "Exposure is the share of projects enrolled (sticky per project; 0 is off). Inside that share every turn rolls twice: did the trigger fire, and given that, is the text sent.",
  logic:
    "The share of the population the code branches differently for; the default arm is the path that ships today, byte for byte.",
  config:
    "The share of the population that reads a different value for the setting under test; the default arm reads whatever the environment says.",
};

type Props = {
  record: AdminExperimentRecord;
  canAct: boolean;
  /** Said only where there is room for it: the detail card, not a table row. */
  accessKnown?: boolean;
  /** Compact drops the blurb and the heading, for the expanded register row. */
  compact?: boolean;
  /** An operator is LOOKING at this control, so read the allocation live rather
   *  than sitting on the snapshot's copy. False where it is mounted but nobody
   *  has opened it - a closed board card - because the board mounts one per
   *  experiment and "always" means ninety-five requests per page load. Moving a
   *  slider reads live regardless of this: see `liveAllocationKey`. */
  focused?: boolean;
  /** Called after a successful move, so a row can collapse itself. */
  onApplied?: () => void;
};

export function AllocationEditor({
  record,
  canAct,
  accessKnown = false,
  compact = false,
  focused = true,
  onApplied,
}: Props) {
  const kind = allocatableKind(record.kind);
  const queryClient = useQueryClient();
  // null = the sliders sit where the server says. Otherwise: ONLY the dials this
  // operator has moved, as an overlay on the server's current values - never a
  // whole allocation of its own.
  //
  // That distinction is the write path's correctness, not a style choice. A move
  // POSTs an ABSOLUTE allocation, so a draft holding every dial would carry the
  // dials nobody touched at whatever value they had when the drag started, and
  // applying it would silently revert a colleague's move made since. As an
  // overlay, an untouched dial always reads through to the freshest base the
  // component has, which is the live one by the time Apply is reachable.
  const [moves, setMoves] = useState<AllocationDraft | null>(null);
  const [reason, setReason] = useState("");
  // Reaching for a dial arms the live read; see `liveAllocationKey`. Deliberately
  // the POINTER, not the first value change: the read is then already in flight
  // while the operator drags, so the move is described against the server's real
  // current value the moment there is a move to describe.
  const [engaged, setEngaged] = useState(false);
  // "" when this control has no business asking the server; see `liveAllocationKey`
  // for which of `focused` and `touched` buys the request, and why.
  const liveKey = liveAllocationKey(record.key, kind, {
    focused,
    touched: engaged || moves !== null,
  });
  const current = useQuery(allocationQuery(liveKey, kind ?? ""));

  const apply = useMutation({
    mutationFn: (body: { kind: AllocatableKind; payload: AllocationDraft; reason: string }) =>
      setAllocation(record.key, body),
    onSuccess: (fresh) => {
      setMoves(null);
      setReason("");
      queryClient.setQueryData(adminKeys.experimentationAllocation(record.key, kind ?? ""), fresh);
      // The snapshot, the ledger and this allocation share one prefix; the
      // prompt-section console and the legacy dial list read the same numbers
      // through their own routes.
      void queryClient.invalidateQueries({ queryKey: adminKeys.experimentation });
      void queryClient.invalidateQueries({ queryKey: adminKeys.promptSections() });
      void queryClient.invalidateQueries({ queryKey: adminKeys.experiments() });
      onApplied?.();
    },
  });

  if (!kind) {
    // Every registry kind is allocatable now, so this is a record of a kind
    // nothing in this build knows: say so rather than drawing a dead slider.
    return (
      <p className="exa__none">
        A <code>{record.kind}</code> experiment has no control in this build.
      </p>
    );
  }

  // The live allocation wins over the snapshot's copy where it was read at all:
  // the snapshot is up to half an hour old and this control is where an operator
  // checks a move took. Where it was not (an untouched, unopened board card), the
  // snapshot IS the answer and the sliders show it.
  const base = allocationDraftFrom(kind, record.arms, current.data?.payload ?? record.allocation);
  // The overlay, applied. An untouched dial reads through to `base`, so when the
  // live allocation lands mid-drag - or a colleague moves a different dial of the
  // same experiment - this picks it up instead of pinning it to whatever it was
  // when the drag started. `describeAllocationMove` below therefore names the real
  // delta, and Apply posts it.
  const shown = overlayAllocation(base, moves);
  const moved = moves !== null && allocationMoved(shown, base);
  const problem = validateAllocation(kind, shown);
  // `current.isSuccess` is a WRITE gate, not a rendering one: until the live read
  // has landed, `base` is still the worker's tick and the absolute payload built
  // on it is a guess. The sliders stay usable throughout (the read is already in
  // flight, armed by the pointer); it is only Apply that waits, and the pending
  // row says so rather than presenting a dead button.
  const ready = canAct && moved && !problem && reason.trim().length >= 3 && current.isSuccess;
  const names = Object.keys(base);

  return (
    <div className={compact ? "exa exa--compact" : "exa"}>
      {compact ? null : (
        <p className="ad-section__sub">
          {KIND_BLURB[kind]}
          {accessKnown && !canAct
            ? " Your access is read only, so the sliders are disabled."
            : null}
        </p>
      )}
      {current.isError ? (
        <p className="exp-section__error">
          Live allocation unavailable ({errorMessage(current.error)}); the sliders show the
          snapshot.
        </p>
      ) : null}

      <div
        className="exa__dials"
        // One pair of handlers for both inputs of every row: React's onFocus
        // bubbles (focusin semantics) and so does onPointerDown, so this is the
        // whole "somebody has reached for a dial" signal.
        onPointerDown={() => setEngaged(true)}
        onFocus={() => setEngaged(true)}
      >
        {names.map((name) => (
          <div className="exa__row" key={name}>
            <span className="exa__name">
              {nameLabel(kind, name)}
              <span className="exa__hint">{hintFor(kind, name, record)}</span>
            </span>
            <input
              className="exa__slider"
              type="range"
              min={0}
              max={100}
              step={1}
              value={shown[name] ?? 0}
              disabled={!canAct || apply.isPending}
              aria-label={`${record.key} ${nameLabel(kind, name)}`}
              onChange={(e) => setMoves({ ...(moves ?? {}), [name]: Number(e.target.value) })}
            />
            <input
              className="exa__number"
              type="number"
              min={0}
              max={100}
              step={1}
              value={shown[name] ?? 0}
              disabled={!canAct || apply.isPending}
              aria-label={`${record.key} ${nameLabel(kind, name)} percent`}
              onChange={(e) => setMoves({ ...(moves ?? {}), [name]: Number(e.target.value) })}
            />
          </div>
        ))}
      </div>

      <ProjectionTable record={record} kind={kind} draft={shown} moved={moved} />

      {kind === "injection" ? (
        <>
          <ShadowPreset
            dials={shown}
            disabled={!canAct || apply.isPending}
            onApply={(next) => setMoves(next)}
          />
          <ExplorationNote dials={shown} />
        </>
      ) : null}

      {problem ? <p className="exa__warn">{problem}</p> : null}

      {moved ? (
        <div className="exa__pending" role="status">
          <span className="exa__pending-text">
            {describeAllocationMove(kind, shown, base)}
            {current.isSuccess
              ? ""
              : current.isError
                ? " Cannot apply: the current allocation could not be read, so this move has nothing reliable to set it from."
                : " Reading the current allocation."}
          </span>
          <input
            className="exa__reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why? (kept in the audit trail)"
            aria-label={`Reason for the ${record.key} allocation change`}
            disabled={apply.isPending}
          />
          <Button
            size="sm"
            tone="accent"
            disabled={!ready || apply.isPending}
            onClick={() => apply.mutate({ kind, payload: shown, reason: reason.trim() })}
          >
            {apply.isPending ? "Applying" : "Apply"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            tone="neutral"
            disabled={apply.isPending}
            onClick={() => {
              setMoves(null);
              setReason("");
              apply.reset();
            }}
          >
            Reset
          </Button>
        </div>
      ) : null}

      {apply.isError ? <p className="exp-section__error">{errorMessage(apply.error)}</p> : null}
      {/* WHO set it comes from the live read and NOWHERE else: the snapshot carries the
          allocation but not its audit half. So this waits for an ANSWER - not merely for
          a control that reads live - because "nobody has moved this" is a claim, and
          making it while the read is still in flight, or after it failed, or on a closed
          board card that never asked, is the same confident-but-wrong sentence about an
          experiment somebody moved this morning. The sliders still show the allocation
          throughout; only the line about authorship waits. */}
      {!current.isSuccess ? null : current.data?.setBy ? (
        <p className="exa__audit">
          Last set by {current.data.setBy}
          {current.data.reason ? `: ${current.data.reason}` : ""}.
        </p>
      ) : (
        <p className="exa__audit">Nobody has moved this; it sits at its code default.</p>
      )}
    </div>
  );
}

/** What a dial move WOULD do, beside what it does now.
 *
 *  Three columns and no request: the share each arm holds today, the share it
 *  would hold if this draft were applied, and how many units are in it right now.
 *  The projection is arithmetic over the draft (`projectedShares`), and the unit
 *  counts are read from the snapshot the page already has, because counting arm
 *  members is a whole-population aggregate and that belongs on the worker's tick,
 *  not behind a slider somebody is dragging. */
function ProjectionTable({
  record,
  kind,
  draft,
  moved,
}: {
  record: AdminExperimentRecord;
  kind: AllocatableKind;
  draft: AllocationDraft;
  moved: boolean;
}) {
  const now = armShares(record);
  if (now.length === 0) return null;
  const next = projectedShares(kind, record.arms, draft);
  const exposures = record.registerFacts?.exposures ?? {};
  const measured = Object.keys(exposures).length > 0;

  return (
    <table className="exp-table exa__projection">
      <TableExport name="experiment-allocation" label="the allocation projection" />
      <thead>
        <tr>
          <th scope="col">Arm</th>
          <th scope="col" className="exp-table__num">
            Now
          </th>
          <th scope="col" className="exp-table__num">
            {moved ? "Would be" : "Draft"}
          </th>
          <th scope="col" className="exp-table__num">
            Units in it
          </th>
        </tr>
      </thead>
      <tbody>
        {now.map((share) => {
          const after = next[share.arm] ?? 0;
          const delta = after - share.pct;
          return (
            <tr key={share.arm} className={delta === 0 ? undefined : "exa__projection--moved"}>
              <td>
                <code>{share.arm}</code>
                {share.baseline ? <span className="exa__baseline">baseline</span> : null}
              </td>
              <td className="exp-table__num">{share.pct}%</td>
              <td className="exp-table__num">
                {after}%
                {delta === 0 ? null : (
                  <span className="exa__delta">{delta > 0 ? `+${delta}` : delta}</span>
                )}
              </td>
              {/* A count the tick has not carried yet is "-", never 0: nobody
                  should read "no users in this arm" off an unmeasured cell. */}
              <td className="exp-table__num">
                {measured ? formatCalls(exposures[share.arm] ?? 0) : "-"}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** The name beside a slider. A config arm is labelled with the VALUE it sets,
 *  because "restructured" says nothing about what the setting becomes and the
 *  point of a config experiment is the value. */
function nameLabel(kind: AllocatableKind, name: string): string {
  if (kind === "injection") {
    return INJECTION_DIAL_LABELS[name as keyof typeof INJECTION_DIAL_LABELS] ?? name;
  }
  if (kind === "section" && name === SECTION_DIAL_KEY) return "Share who get the section";
  return name;
}

function hintFor(kind: AllocatableKind, name: string, record: AdminExperimentRecord): string {
  if (kind === "injection") {
    if (name === "exposure") return "share of projects enrolled; 0 is off";
    if (name === "p_triggered") return "of enrolled turns where the trigger fired";
    return "of enrolled turns where it did not";
  }
  if (kind === "section") return `${record.arms[1] ?? "absent"} takes the rest`;
  if (kind === "config") return `share reading this arm's value for the setting`;
  return `share of ${record.unit}s on this arm`;
}
